import { FC, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { useAppSelector } from '../../../hooks/redux';
import { withHoverIcon } from '../../../hoc/withHoverIcon';
import Button from '../../common/button';
import { CloseCircleCrossBtn } from '../../icons/close-circle-cross-btn';
import { IMacCardInfo } from '../../../store/mac.slice';

import { MacCardsSelected } from './mac-card-selected';

const CloseCircleCrossBtnHover = withHoverIcon(CloseCircleCrossBtn);

const CLOSE_BTN_COLOR_PROPS = {
  defaultFill: '#828282',
  defaultFillBg: 'rgba(255, 255, 255, 0.8)',
  hoverFill: '#63656A',
  hoverFillBg: '#DEC05E',
};

export interface MacGroupPsychologistResultsProps {
  /** Список identity участников на встрече (кроме психолога) — ждём ответа от каждого */
  expectedParticipantIds: string[];
  /** Имена для отображения в табах (identity -> displayName) */
  participantDisplayNames?: Record<string, string>;
  onClose: () => void;
  /** Панель развёрнута на всю высоту поверх видео */
  isExpanded?: boolean;
  /** Переключить развёрнутое состояние */
  onExpandToggle?: () => void;
  /** Вызывается при переключении таба: номер участника и его карты — для трансляции клиентам */
  onActiveTabChange?: (participantLabel: string, cards: IMacCardInfo[]) => void;
}

const GROUP_MAC_INSTRUCTIONS = [
  {
    title: 'Если карты рубашкой вверх:',
    lines: [
      'Колода закрыта.',
      'При выборе карта подсвечивается жёлтым, но остаётся закрытой.',
      'Можно выбрать до 4 карт.',
      'Когда клиент закончит выбор, он нажимает «Выбрать» — карты открываются у вас и клиента. Остальные скрываются.',
    ],
  },
  { title: 'Завершение работы:', lines: ['Нажмите Х (крестик).'] },
  { title: 'Новый расклад:', lines: ['Повторно нажмите иконку МАК-карт в нижней части экрана.'] },
];

const GROUP_MAC_IMPORTANT_TEXT =
  'ВАЖНО! Если карты выбираются в режиме рубашкой вверх, то они не должны переворачиваться, клиент увидит их в открытом виде после выбора. А если они показываются в режиме рубашкой вниз, то клиент видит изображение и подсвечиваются выбранные карты.';

/**
 * Нижняя панель психолога в групповой терапии после «Показать колоду»:
 * Сверху — счётчик ответивших, инструкции и «Клиенты думают…» пока не все ответили; затем табы с результатами.
 */
export const MacGroupPsychologistResults: FC<MacGroupPsychologistResultsProps> = ({
  expectedParticipantIds,
  participantDisplayNames,
  onClose,
  isExpanded = false,
  onExpandToggle,
  onActiveTabChange,
}) => {
  const clientSelectionsByParticipant = useAppSelector((state) => state.macStateSlice.clientSelectionsByParticipant);

  const respondedIds = expectedParticipantIds.filter((id) => clientSelectionsByParticipant[id]?.length > 0);
  const allResponded = expectedParticipantIds.length > 0 && respondedIds.length >= expectedParticipantIds.length;
  const [forceShowResults, setForceShowResults] = useState(false);
  const canShowResults = allResponded || forceShowResults;

  const [activeTabId, setActiveTabId] = useState<string | null>(null);

  const effectiveActiveId =
    activeTabId && respondedIds.includes(activeTabId) ? activeTabId : (respondedIds[0] ?? null);

  const participantNumber = (index: number) => index + 1;

  const initialTabSyncDone = useRef(false);
  useEffect(() => {
    if (!canShowResults || respondedIds.length === 0 || !onActiveTabChange) return;
    if (initialTabSyncDone.current) return;
    initialTabSyncDone.current = true;
    const firstId = respondedIds[0];
    setActiveTabId(firstId);
    const idx = expectedParticipantIds.indexOf(firstId);
    const numLabel = String(participantNumber(idx >= 0 ? idx : 0));
    const labelForClient = participantDisplayNames?.[firstId] ?? numLabel;
    const cards = clientSelectionsByParticipant[firstId] ?? [];
    onActiveTabChange(labelForClient, cards);
  }, [canShowResults, respondedIds, expectedParticipantIds, clientSelectionsByParticipant, participantDisplayNames, onActiveTabChange]);

  return (
    <div className="relative font-manrope flex flex-col h-full min-h-0 bg-white rounded-[5px] md:rounded-[15px] overflow-hidden">
      <div className="absolute top-5 right-5 flex items-center gap-2 z-[40] pointer-events-auto">
        {onExpandToggle && (
          <button
            type="button"
            onClick={onExpandToggle}
            className="pointer-events-auto flex items-center gap-1.5 text-[#2C3038] hover:text-[#585858] text-sm font-manrope px-2 py-1 rounded hover:bg-[#E5E5E5]"
            aria-label={isExpanded ? 'Свернуть панель' : 'Расширить панель'}
          >
            {isExpanded ? (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zm-3-12v2h3v3h2V5h-5z" />
                </svg>
                Свернуть
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zm-3-12v2h3v3h2V5h-5z" />
                </svg>
                Расширить
              </>
            )}
          </button>
        )}
        <button type="button" onClick={onClose} className="pointer-events-auto cursor-pointer" aria-label="Закрыть">
          <CloseCircleCrossBtnHover width={30} height={30} className="" {...CLOSE_BTN_COLOR_PROPS} />
        </button>
      </div>
      <div
        className={classNames(
          'relative flex items-center gap-3 flex-shrink-0 p-4 pb-2',
          !canShowResults && 'border-b border-[#E5E5E5]',
        )}
      >
        <div className="flex items-baseline gap-3 min-w-0 flex-1 flex-wrap">
          <h2 className="font-manrope font-bold text-2xl text-[#2C3038] shrink-0">МАК-карты</h2>
          {canShowResults && (
            <p className="font-manrope text-[#585858] text-lg truncate min-w-0">
              Клиенты сделали свой выбор. Выберите участника, чтобы посмотреть результат.
            </p>
          )}
        </div>
        {!canShowResults && expectedParticipantIds.length > 0 && (
          <div className="flex items-center gap-3 shrink-0">
            <p className="font-manrope text-[#2C3038] text-lg font-medium tabular-nums">
              Ответили: {respondedIds.length} из {expectedParticipantIds.length}
            </p>
            {respondedIds.length > 0 && (
              <Button type="primary" className="!text-sm py-1.5 px-3" onClick={() => setForceShowResults(true)}>
                Показать результаты
              </Button>
            )}
          </div>
        )}
        <div className="flex-1 min-w-0" aria-hidden />
      </div>

      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        {!canShowResults ? (
          <>
            {/* Инструкции и сообщение «Клиенты думают…» — при переполнении прокрутка */}
            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 py-4 white-scroll">
              <div className="flex flex-col gap-5 text-[#2C3038] text-lg font-manrope">
                {GROUP_MAC_INSTRUCTIONS.map((block, i) => (
                  <div key={i}>
                    <p className="font-bold mb-1.5 text-[1.125rem]">{block.title}</p>
                    <ul className="list-disc list-inside space-y-1 text-[#585858] text-lg">
                      {block.lines.map((line, j) => (
                        <li key={j}>{line}</li>
                      ))}
                    </ul>
                  </div>
                ))}
                <p className="font-bold text-[#2C3038] text-lg">{GROUP_MAC_IMPORTANT_TEXT}</p>
              </div>
            </div>
            <div className="flex-shrink-0 px-4 pb-4 flex flex-col items-center gap-2">
              <span className="inline-flex items-center px-10 py-2 rounded-[10px] bg-[#C00000]/50 text-white text-lg font-manrope font-medium">
                Клиенты думают...
              </span>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 pb-3 mb-3 shrink-0 px-4">
              {respondedIds.map((id) => {
                const idx = expectedParticipantIds.indexOf(id);
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => {
                      setActiveTabId(id);
                      const numLabel = String(participantNumber(idx >= 0 ? idx : 0));
                      const labelForClient = participantDisplayNames?.[id] ?? numLabel;
                      const cards = clientSelectionsByParticipant[id] ?? [];
                      onActiveTabChange?.(labelForClient, cards);
                    }}
                    title={participantDisplayNames?.[id]}
                    className={classNames(
                      'flex items-center justify-center w-9 h-9 rounded-full text-lg font-medium transition-colors',
                      effectiveActiveId === id
                        ? 'bg-[#C00000] text-white border-2 border-white'
                        : 'bg-[#E5E5E5] text-[#2C3038] hover:bg-[#D5D5D5]',
                    )}
                  >
                    {participantNumber(idx >= 0 ? idx : 0)}
                  </button>
                );
              })}
            </div>
            <div className="flex-1 min-h-0 overflow-hidden flex flex-col px-4 pb-4">
              {effectiveActiveId && clientSelectionsByParticipant[effectiveActiveId]?.length > 0 ? (
                <div className="flex-1 min-h-0 flex flex-col">
                  <MacCardsSelected
                    selectedCards={clientSelectionsByParticipant[effectiveActiveId]}
                    fillContainerHeight
                  />
                </div>
              ) : (
                <p className="font-manrope text-[#585858] text-lg">
                  {respondedIds.includes(effectiveActiveId ?? '') ? 'Нет выбранных карт' : 'Участник ещё не ответил'}
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
